import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { billOfMaterialsIdFormat } from "@redotech/redo-fulfillment-id";
import {
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { productIdFormat } from "@redotech/redo-product-id";
import { privacy } from "@redotech/schema-privacy";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FulfillmentGroupData } from "./fulfillment-group";

const FulfillmentOrderLineItemSchemaBase = z.object({
  externalId: z.string().meta({ privacy: privacy.id.external.resource }), // SHOULD BE GLOBALLY UNIQUE
  imageUrl: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
  customAttributes: z
    .array(
      z
        .object({
          key: z.string().meta({ privacy: privacy.nonPii }),
          value: z.string().meta({ privacy: privacy.text.customer }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
  // Redo business logic should only use quantity
  // unfulfilledQuantity and totalQuantity are provided by the marketplace and are used
  // in upsert logic to calculate the "quantity" of items when we split fulfillment orders across
  // multiple fulfillment groups (ex. partial fulfillments)
  totalQuantity: z.number().meta({ privacy: privacy.nonPii }),
  unfulfilledQuantity: z.number().meta({ privacy: privacy.nonPii }),
  quantity: z.number().meta({ privacy: privacy.nonPii }),
  weight: z
    .object({
      unit: createEnum(WeightUnit, { enumName: "WeightUnit" }).meta({
        privacy: privacy.nonPii,
      }),
      value: z.number().meta({ privacy: privacy.nonPii }),
    })
    .meta({ privacy: privacy.structured }),
  // These three IDs are nullish because if the product is deleted they all become null
  productId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  inventoryItemId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  redoProductId: productIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  isExchangeItem: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  redoMarketingName: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  inventoryItemWeight: WeightSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  lineItemId: z.string().meta({ privacy: privacy.nonPii }),
  amazonBuyLineItemId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  title: z.string().meta({ privacy: privacy.nonPii }),
  productTitle: z.string().meta({ privacy: privacy.nonPii }),
  variantTitle: z.string().meta({ privacy: privacy.nonPii }),
  variantInventoryQuantity: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  selectedOptions: z
    .array(
      z
        .object({
          name: z.string().meta({ privacy: privacy.nonPii }),
          value: z.string().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  requiresShipping: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  sku: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  unitPrice: MoneySchema.meta({ privacy: privacy.structured }),
  unitDiscountedPrice: MoneySchema.meta({ privacy: privacy.structured }),
  totalPrice: MoneySchema.meta({ privacy: privacy.structured }),
  totalDiscountedPrice: MoneySchema.meta({ privacy: privacy.structured }),
  totalTax: MoneySchema.meta({ privacy: privacy.structured }),
  hsCode: z.string().meta({ privacy: privacy.nonPii }).nullable(),
  customsDescription: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  })
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  bin: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  barcode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  barcodes: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  productTags: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  vendor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  description: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  notReceived: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomDate1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomDate2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomDate3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomString1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomString2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomString3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomNumber1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomNumber2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  productCustomNumber3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomDate1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomDate2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomDate3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomString1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomString2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomString3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomNumber1: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomNumber2: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  variantCustomNumber3: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  orderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  lineItemNumber: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  preSplitUnfulfilledQuantity: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .nullish(), // Used to ensure that we dont duplicate line items for split orders
  dimValue: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  productType: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  imsInventoryItemId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  shouldBeInOwnPackage: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  // References the lineItemId of this item's bundle parent within the same
  // fulfillment order — set when the platform sent the bundle as flat sibling
  // rows (e.g. BigCommerce parent_order_product_id, or Shopify bundle-app
  // custom attributes). Presence of a value marks this line as a bundle
  // component. Redo BOM relationships are nested in billOfMaterialsInfos
  // instead.
  bundleParentLineItemId: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  onHandInventory: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  unitCost: z.number().meta({ privacy: privacy.nonPii }).nullish(),
});

const LineItemLotSchema_ = z.object({
  lotNumber: z.string().meta({ privacy: privacy.nonPii }).nullable(),
  expirationDate: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }).nullable(),
  quantity: z.number().meta({ privacy: privacy.nonPii }).int(),
});
export const LineItemLotSchema: z.ZodType<LineItemLot> = LineItemLotSchema_;
export type LineItemLot = z.infer<typeof LineItemLotSchema_>;

export interface BillOfMaterialsInfo {
  bomId: z.infer<typeof billOfMaterialsIdFormat.schema>;
  expandedQuantity: number;
  expansionItems: FulfillmentOrderLineItem[];
  calculatedAt?: Date;
  /** When true, a work order flow handles this BOM's inputs — do not expand for picking */
  hasWorkOrderFlow?: boolean;
  leftovers?: {
    redoProductId: z.infer<typeof productIdFormat.schema>;
    quantity: number;
  }[];
}

/**
 * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion via `billOfMaterialsInfos`.
 * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems()` to get only the leaf node items.
 * In most cases (splitting, table filtering, etc) - you probably want to use the line items as-is - the actual line items that were ordered.
 */
export type FulfillmentOrderLineItem = z.infer<
  typeof FulfillmentOrderLineItemSchemaBase
> & { billOfMaterialsInfos?: BillOfMaterialsInfo[] };

// Extend base schema with billOfMaterialsInfos (must use z.custom to handle recursive reference)
export const FulfillmentOrderLineItemSchema_ =
  FulfillmentOrderLineItemSchemaBase.extend({
    // Use z.custom with JSON annotation to prevent recursive schema extraction in DDL generator
    // while maintaining correct TypeScript types. The recursive structure will be stored as JSONB.
    billOfMaterialsInfos: z
      .custom<BillOfMaterialsInfo[]>()
      .meta({ annotation: "JSON", privacy: privacy.untypedPayload })
      .optional(),
  });

// Export with type annotation for type safety
export const FulfillmentOrderLineItemSchema: z.ZodType<FulfillmentOrderLineItem> =
  FulfillmentOrderLineItemSchema_;

export interface IFulfillmentOrderLineItem extends z.ZodType<FulfillmentOrderLineItem> {}

export type FulfillmentOrderLineItemWithOrderIds = FulfillmentOrderLineItem & {
  orderNames: string[];
};

/**
 * Regenerates missing/duplicated lineItemIds (duplicates break arrayFilter
 * updates) while preserving unique ones so aligned copies stay aligned.
 */
export function ensureUniqueLineItemIds<T extends { lineItemId: string }>(
  items: readonly T[],
): T[] {
  const seen = new Set<string>();
  return items.map((item) => {
    const lineItemId =
      item.lineItemId && !seen.has(item.lineItemId)
        ? item.lineItemId
        : crypto.randomUUID();
    seen.add(lineItemId);
    return { ...item, lineItemId };
  });
}

export interface LineItemGroupingConfig {
  usePrice?: boolean;
  degroupByCustomAttributes?: boolean;
  fallbackToTitle?: boolean;
  preferRedoProductId?: boolean;
}

// Builds a stable fingerprint of a line item's custom attributes for grouping.
// Set excludeInternal to drop `_`-prefixed attributes (Shopify's internal/hidden
// attrs like cart tokens) that would otherwise over-split rows.
export function getCustomAttributesKey(
  customAttributes: readonly { key: string; value: string }[],
  config?: { excludeInternal?: boolean },
): string {
  return [...customAttributes]
    .filter((attr) => !config?.excludeInternal || !attr.key.startsWith("_"))
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((attr) => `${attr.key}:${attr.value}`)
    .join("|");
}

export function getLineItemGroupingKey(
  item: FulfillmentOrderLineItem,
  config?: LineItemGroupingConfig,
): string {
  if (config?.preferRedoProductId && item.redoProductId != null) {
    let key = `redo-product:${item.redoProductId}`;
    if (config.degroupByCustomAttributes) {
      key += `-${getCustomAttributesKey(item.customAttributes)}`;
    }
    return key;
  }
  if (!item.variantId || !item.unitDiscountedPrice.amount) {
    if (config?.fallbackToTitle) {
      return item.title;
    }
    return item.externalId;
  }
  const customAttributesKey = getCustomAttributesKey(item.customAttributes);

  let key = `${item.variantId}`;
  if (config?.usePrice) {
    key += `-${item.unitDiscountedPrice.amount}`;
  }
  if (config?.degroupByCustomAttributes) {
    key += `-${customAttributesKey}`;
  }
  return key;
}

// We could have the same item purchased on multiple orders that get merged together - this should reduce those line items to a single line item
export function groupFulfillmentOrderLineItems<
  T extends FulfillmentOrderLineItem & { lots?: LineItemLot[] },
>(items: readonly T[], config?: LineItemGroupingConfig): T[] {
  const reducedLineItems: T[] = [];
  const reducedLineItemsRec: Record<string, T> = {};

  for (const item of items) {
    const key = getLineItemGroupingKey(item, config);

    if (!reducedLineItemsRec[key]) {
      reducedLineItemsRec[key] = { ...item };
    } else {
      reducedLineItemsRec[key].totalQuantity += item.totalQuantity;
      reducedLineItemsRec[key].unfulfilledQuantity += item.unfulfilledQuantity;
      reducedLineItemsRec[key].quantity += item.quantity;
      if (item.lots?.length) {
        reducedLineItemsRec[key].lots = mergeLots([
          ...(reducedLineItemsRec[key].lots ?? []),
          ...item.lots,
        ]);
      }
    }
  }

  return [...reducedLineItems, ...Object.values(reducedLineItemsRec)];
}

function mergeLots(lots: LineItemLot[]): LineItemLot[] {
  const byKey = new Map<string, LineItemLot>();
  for (const lot of lots) {
    const key = `${lot.lotNumber ?? ""}|${lot.expirationDate?.getTime() ?? ""}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.quantity += lot.quantity;
    } else {
      byKey.set(key, { ...lot });
    }
  }
  return Array.from(byKey.values());
}

// Given a list of line items grouped together but that may not be grouped by fulfillment order, return a list of line items ungrouped. The list will only have one entry for each fulfillment order line item id
export function ungroupFulfillmentOrderLineItems(
  originalItems: FulfillmentOrderLineItem[],
  groupedItems: FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  const ungrouped: FulfillmentOrderLineItem[] = [];

  for (const originalItem of originalItems) {
    const matchingGroupedItem = groupedItems.find(
      (i) =>
        !!i.variantId &&
        i.variantId === originalItem.variantId &&
        i.unitDiscountedPrice.amount ===
          originalItem.unitDiscountedPrice.amount,
    );
    if (matchingGroupedItem && matchingGroupedItem.quantity > 0) {
      const quantityToTake = Math.min(
        originalItem.quantity,
        matchingGroupedItem.quantity,
      );

      // Remove the quantity from the grouped item and add that to the ungrouped item
      matchingGroupedItem.quantity -= quantityToTake;
      ungrouped.push({ ...originalItem, quantity: quantityToTake });
    }
  }

  for (const groupedItem of groupedItems) {
    if (groupedItem.quantity > 0) {
      ungrouped.push(groupedItem);
    }
  }

  return ungrouped;
}

export function getLineItemKey(item: FulfillmentOrderLineItem): string {
  return `${item.productId}${item.variantId ? `-${item.variantId}` : ""}`;
}

export function calculatePickedQuantitiesByGroupingKey(
  items: readonly FulfillmentOrderLineItem[],
  pickedQuantityByExternalId: Map<string, number>,
  config?: { usePrice?: boolean; degroupByCustomAttributes?: boolean },
): Map<string, number> {
  const pickedByGroupingKey = new Map<string, number>();

  for (const item of items) {
    const groupingKey = getLineItemGroupingKey(item, config);
    const pickedQty = pickedQuantityByExternalId.get(item.externalId) || 0;
    pickedByGroupingKey.set(
      groupingKey,
      (pickedByGroupingKey.get(groupingKey) || 0) + pickedQty,
    );
  }

  return pickedByGroupingKey;
}

export function getPickedMapByExternalId(
  fulfillmentGroup: FulfillmentGroupData,
): Map<string, number> {
  const pickSessionItemStatuses =
    fulfillmentGroup.pickSessionItemStatuses || [];
  const pickedMap = new Map<string, number>();

  for (const status of pickSessionItemStatuses) {
    pickedMap.set(
      status.fulfillmentOrderLineItemExternalId,
      status.quantityPicked,
    );
  }

  return pickedMap;
}

export function getMissingPickedItems(
  fulfillmentGroup: FulfillmentGroupData,
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  const pickedByExternalId = getPickedMapByExternalId(fulfillmentGroup);

  const pickableItems = expandLineItemsToPickableItems(
    fulfillmentGroup.items,
    options,
  );
  const missingItems: FulfillmentOrderLineItem[] = [];
  for (const item of pickableItems) {
    // If item is not in pickSessionItemStatuses, treat as unpicked (quantity 0)
    const pickedQty = pickedByExternalId.get(item.externalId) || 0;

    if (pickedQty < item.quantity) {
      missingItems.push({ ...item, quantity: item.quantity - pickedQty });
    }
  }

  // Expand for consistency with item name column so that we show the total quantity of items in the column
  // We cannot use getExpandedItems function because it has a cache for the fulfillment group items and we need to display a subset of the items
  const expanded = missingItems.flatMap((item) =>
    Array.from({ length: item.quantity }, () => ({
      ...item,
      quantity: 1,
      totalQuantity: 1,
    })),
  );

  return expanded;
}

/**
 * Recursively collects pickable items from a tree structure.
 * For partially expanded BOMs (expandedQuantity < quantity), the parent item
 * is also returned as a pickable item with the non-expanded quantity.
 *
 * @param item - The item to extract pickable items from
 * @param options.hideInputItems - When true, treat every BOM expansion the same
 *   as a work-order-handled BOM: keep the parent output as the pickable item
 *   and skip descending into the expansion inputs.
 * @returns Array of pickable items
 */
function collectBomLeafNodes(
  item: FulfillmentOrderLineItem,
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  const bomInfos = item.billOfMaterialsInfos;
  const hasChildren = bomInfos?.some((info) => info.expansionItems.length > 0);

  if (!hasChildren) {
    return [item];
  }

  const results: FulfillmentOrderLineItem[] = [];

  // Count only non-work-order expansions toward the expanded quantity.
  // Work-order BOMs keep the output product as the pickable item, so their
  // expandedQuantity should remain as parent (output) quantity, not be subtracted.
  // When hideInputItems is on, treat every BOM the same way.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const expandedQtyWithoutWorkOrders = bomInfos!.reduce(
    (sum, info) =>
      sum +
      (info.hasWorkOrderFlow || options.hideInputItems
        ? 0
        : info.expandedQuantity),
    0,
  );
  const nonExpandedQty = item.quantity - expandedQtyWithoutWorkOrders;
  if (nonExpandedQty > 0) {
    results.push({
      ...item,
      quantity: nonExpandedQty,
      totalQuantity: nonExpandedQty,
      unfulfilledQuantity: nonExpandedQty,
      billOfMaterialsInfos: undefined,
    });
  }

  // Collect leaf nodes from non-work-order BOM expansions' children
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  for (const bomInfo of bomInfos!) {
    // Skip expansions handled by work orders — the output product is included
    // above as part of the non-expanded parent quantity. Same when the
    // hideInputItems setting is on.
    if (bomInfo.hasWorkOrderFlow || options.hideInputItems) {
      continue;
    }
    results.push(
      ...bomInfo.expansionItems.flatMap((child) =>
        collectBomLeafNodes(child, options),
      ),
    );
  }

  return results;
}

/**
 * Extracts only the pickable items from BOM expanded line items.
 * When items have billOfMaterialsInfos (bundles/kits), returns the actual components to pick.
 * For partially expanded items (expandedQuantity < quantity), the parent item
 * is also returned as a pickable item with the non-expanded quantity.
 *
 * Example (full expansion):
 * - Item A (qty=2, expandedQuantity=2) -> [B, C]
 * Result: [B, C]
 *
 * Example (partial expansion):
 * - Item A (qty=3, expandedQuantity=1) -> [B, C]
 * Result: [A (qty=2), B, C]
 *
 * @param items - Array of line items that may have BOM expansion
 * @param options.hideInputItems - When true, do not expand BOMs into their
 *   input items (keep the parent output item as pickable). Required so callers
 *   must explicitly opt in or out — display paths typically read this from the
 *   team's `outboundLabels.billOfMaterials.alwaysHideInputItems` setting,
 *   while inventory/correctness paths must always pass `false`.
 * @returns Array of only the leaf node items (actual components to pick)
 */
export function expandLineItemsToPickableItems(
  items: readonly FulfillmentOrderLineItem[],
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  return items.flatMap((item) => collectBomLeafNodes(item, options));
}

export type BomLeftover = NonNullable<BillOfMaterialsInfo["leftovers"]>[number];

/**
 * Recursively collects leftovers from a single line item's BOM tree.
 */
function collectBomLeftoversFromItem(
  item: FulfillmentOrderLineItem,
): BomLeftover[] {
  const bomInfos = item.billOfMaterialsInfos;
  if (!bomInfos) {
    return [];
  }

  const results: BomLeftover[] = [];

  for (const bomInfo of bomInfos) {
    if (bomInfo.leftovers) {
      results.push(...bomInfo.leftovers);
    }
    for (const child of bomInfo.expansionItems) {
      results.push(...collectBomLeftoversFromItem(child));
    }
  }

  return results;
}

/**
 * Recursively collects all BOM leftovers from a list of line items.
 * Traverses the full BOM tree to find leftovers at every nesting level.
 *
 * @param items - Array of line items that may have BOM expansion with leftovers
 * @returns Array of all leftovers found across all BOM levels
 */
export function collectAllBomLeftovers(
  items: readonly FulfillmentOrderLineItem[],
): BomLeftover[] {
  return items.flatMap((item) => collectBomLeftoversFromItem(item));
}

export interface BundleNode {
  item: FulfillmentOrderLineItem;
  children: BundleNode[];
}

function toBundleNodeRedo(item: FulfillmentOrderLineItem): BundleNode {
  const bomInfos = item.billOfMaterialsInfos;
  if (!bomInfos?.length) {
    return { item, children: [] };
  }
  const children: BundleNode[] = bomInfos.flatMap((info) =>
    info.expansionItems.map(toBundleNodeRedo),
  );
  return { item, children };
}

/**
 * Build the full parent→child relationship tree from both Redo BOM and
 * BigCommerce bundle relationships. Use flattenBundleTreeForDisplay to render.
 * Does NOT call expandLineItemsToPickableItems — do not use for pick/scan paths.
 */
export function toBundleTree(
  items: readonly FulfillmentOrderLineItem[],
): BundleNode[] {
  // Flat-sibling bundles (e.g. BigCommerce): items with bundleParentLineItemId
  // are children. Collect them keyed by parent lineItemId for fast lookup.
  const bundleChildrenByParentId = new Map<
    string,
    FulfillmentOrderLineItem[]
  >();
  const childExternalIds = new Set<string>();
  const allLineItemIds = new Set(items.map((i) => i.lineItemId));

  for (const item of items) {
    if (item.bundleParentLineItemId) {
      const parentId = item.bundleParentLineItemId;
      const list = bundleChildrenByParentId.get(parentId) ?? [];
      list.push(item);
      bundleChildrenByParentId.set(parentId, list);
      // Only suppress the child from the top-level if its parent is present.
      // An orphaned child (parent missing from the fulfillment) falls through
      // as a plain top-level row rather than being silently dropped.
      if (allLineItemIds.has(parentId)) {
        childExternalIds.add(item.externalId);
      }
    }
  }

  const nodes: BundleNode[] = [];

  for (const item of items) {
    // Bundle children are rendered under their parent — skip them at the top
    // level.
    if (childExternalIds.has(item.externalId)) {
      continue;
    }

    const bomInfos = item.billOfMaterialsInfos;
    const hasRedoBom = bomInfos?.some((info) => info.expansionItems.length > 0);

    if (hasRedoBom) {
      // Redo BOM: children are nested inside the item's billOfMaterialsInfos.
      nodes.push(toBundleNodeRedo(item));
    } else {
      // Flat-sibling bundle (or plain item): children live in the flat list.
      const bundleChildren =
        bundleChildrenByParentId.get(item.lineItemId) ?? [];
      nodes.push({
        item,
        children: bundleChildren.map((child) => ({
          item: child,
          children: [],
        })),
      });
    }
  }

  return nodes;
}

/**
 * Drops bundle component lines whose parent (output) line is present in the
 * list — used by output-items display so only the bundle rows remain. An
 * orphaned component (parent missing from this fulfillment, e.g. a split
 * shipment) is kept: hiding it would silently drop the item from the document
 * with no output row representing it.
 */
export function excludeBundleComponents(
  items: readonly FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  const allLineItemIds = new Set(items.map((i) => i.lineItemId));
  return items.filter(
    (item) =>
      !(
        item.bundleParentLineItemId &&
        allLineItemIds.has(item.bundleParentLineItemId)
      ),
  );
}

/**
 * The quantity of an item that actually ships as-is, i.e. was not further
 * expanded into deeper BOM inputs. Mirrors the math in collectBomLeafNodes:
 * work-order expansions keep the output product as the shipped item, so they
 * do not count toward the expanded quantity.
 */
function shippedQuantity(item: FulfillmentOrderLineItem): number {
  const bomInfos = item.billOfMaterialsInfos;
  const hasChildren = bomInfos?.some((info) => info.expansionItems.length > 0);
  if (!hasChildren) {
    return item.quantity;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const expandedQty = bomInfos!.reduce(
    (sum, info) => sum + (info.hasWorkOrderFlow ? 0 : info.expandedQuantity),
    0,
  );
  return item.quantity - expandedQty;
}

/**
 * Flatten a BundleNode tree to a display list with depth annotations.
 * depth 0 = output/parent row; depth 1 = direct child inputs.
 * Nodes deeper than maxDepth are collapsed into their parent's depth level.
 *
 * Depth-0 rows represent the ordered output products and always show their
 * full ordered quantity. Deeper rows represent box contents: a child that was
 * itself fully expanded into deeper inputs never ships, so its row is dropped
 * (its children are promoted to its depth); a partially expanded child shows
 * only the quantity that actually ships.
 */
export function flattenBundleTreeForDisplay(
  nodes: BundleNode[],
  maxDepth = 1,
): { item: FulfillmentOrderLineItem; depth: number }[] {
  const result: { item: FulfillmentOrderLineItem; depth: number }[] = [];

  function visit(node: BundleNode, depth: number) {
    let shown = true;
    if (depth === 0) {
      result.push({ item: node.item, depth });
    } else {
      const shippedQty = shippedQuantity(node.item);
      shown = shippedQty > 0;
      if (shown) {
        result.push({
          item:
            shippedQty === node.item.quantity
              ? node.item
              : {
                  ...node.item,
                  quantity: shippedQty,
                  totalQuantity: shippedQty,
                  unfulfilledQuantity: shippedQty,
                  billOfMaterialsInfos: undefined,
                },
          depth,
        });
      }
    }
    // A dropped row's children take its place at the same depth; shown rows
    // nest their children one level deeper, capped at maxDepth.
    const childDepth = shown ? Math.min(depth + 1, maxDepth) : depth;
    for (const child of node.children) {
      visit(child, childDepth);
    }
  }

  for (const node of nodes) {
    visit(node, 0);
  }

  return result;
}
